"use client";

import { useEffect, useState } from "react";

import { Input, Select } from "@/components/ui-system";
import { cn } from "@/lib/utils/cn";

import {
  type BoolExpected,
  type ChannelRow,
} from "../schema/datalogger.schema";

type ChannelThresholdModalProps = {
  open: boolean;
  onClose: () => void;
  channel: ChannelRow | null;
  onApply: (patch: Partial<ChannelRow>) => void;
};

const control =
  "ui-control w-full rounded-control bg-surface-primary text-foreground shadow-none placeholder:text-muted";

export function ChannelThresholdModal({ open, onClose, channel, onApply }: ChannelThresholdModalProps) {
  const numericLike = Boolean(
    channel && (channel.dataType === "float" || channel.dataType === "int" || channel.dataType === "formula"),
  );

  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [warnLow, setWarnLow] = useState("");
  const [warnHigh, setWarnHigh] = useState("");
  const [boolExpected, setBoolExpected] = useState<BoolExpected>("any");
  const [stringMaxLength, setStringMaxLength] = useState("");

  useEffect(() => {
    if (!open || !channel) {
      return;
    }
    setMinValue(channel.minValue != null ? String(channel.minValue) : "");
    setMaxValue(channel.maxValue != null ? String(channel.maxValue) : "");
    setWarnLow(channel.thresholdWarningLow != null ? String(channel.thresholdWarningLow) : "");
    setWarnHigh(channel.thresholdWarningHigh != null ? String(channel.thresholdWarningHigh) : "");
    setBoolExpected(channel.boolExpected);
    setStringMaxLength(channel.stringMaxLength != null ? String(channel.stringMaxLength) : "");
  }, [open, channel]);

  if (!open || !channel) {
    return null;
  }

  const row = channel;

  function parseNum(raw: string): number | null {
    const t = raw.trim();
    if (t === "") {
      return null;
    }
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }

  function save() {
    if (numericLike) {
      onApply({
        minValue: parseNum(String(minValue)),
        maxValue: parseNum(String(maxValue)),
        thresholdWarningLow: parseNum(String(warnLow)),
        thresholdWarningHigh: parseNum(String(warnHigh)),
      });
    } else if (row.dataType === "bool") {
      onApply({ boolExpected });
    } else {
      const raw = String(stringMaxLength).trim();
      onApply({
        stringMaxLength: raw === "" ? null : Number.isFinite(Number(raw)) ? Number(raw) : row.stringMaxLength,
      });
    }
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-control border border-card-border bg-surface-primary shadow-card"
        role="dialog"
        aria-modal
        aria-labelledby="threshold-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-card-border bg-muted-bg/30 px-ds-6 py-ds-5">
          <h2
            id="threshold-modal-title"
            className="font-sans text-lg font-semibold text-neutral-950 dark:text-foreground"
          >
            Thresholds - {row.name.trim() || row.channelKey}
          </h2>
          <p className="mt-ds-2 text-caption text-neutral-700 dark:text-muted">
            Limits and warnings adapt to the current channel data type.
          </p>
        </div>

        <div className="px-ds-6 py-ds-5">
          {numericLike ? (
            <div className="grid gap-ds-4 rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4 sm:grid-cols-2">
              {(
                [
                  ["minValue", "Minimum", minValue, setMinValue, "Optional lower hard limit."],
                  ["maxValue", "Maximum", maxValue, setMaxValue, "Optional upper hard limit."],
                  ["warnLow", "Warning low", warnLow, setWarnLow, "Early warning before the minimum is breached."],
                  ["warnHigh", "Warning high", warnHigh, setWarnHigh, "Early warning before the maximum is breached."],
                ] as const
              ).map(([key, label, val, setVal, helper]) => (
                <div key={key} className="flex flex-col gap-ds-1.5">
                  <label className="form-label">{label}</label>
                  <Input
                    className={control}
                    inputMode="decimal"
                    value={val === null || val === undefined ? "" : String(val)}
                    onChange={(e) => setVal(e.target.value)}
                  />
                  <span className="form-helper">{helper}</span>
                </div>
              ))}
            </div>
          ) : row.dataType === "bool" ? (
            <div className="max-w-sm rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
              <label className="form-label">Expected state</label>
              <Select
                className={cn(control, "mt-ds-1")}
                value={boolExpected}
                onChange={(e) => setBoolExpected(e.target.value as BoolExpected)}
              >
                <option value="any">Any</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </Select>
              <span className="mt-ds-2 block form-helper">Use this when the signal should normally remain on one stable state.</span>
            </div>
          ) : (
            <div className="max-w-xs rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
              <label className="form-label">Max length</label>
              <Input
                className={cn(control, "mt-ds-1")}
                inputMode="numeric"
                value={stringMaxLength === null || stringMaxLength === undefined ? "" : String(stringMaxLength)}
                onChange={(e) => setStringMaxLength(e.target.value)}
              />
              <span className="mt-ds-2 block form-helper">Leave empty if string values do not need length enforcement.</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-ds-2 border-t border-card-border bg-muted-bg/30 px-ds-6 py-ds-4">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm !no-underline" onClick={save}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
